import axios from 'axios';
import { getAuth } from '../providers/AppAuthProvider';
import { API_BASE_URL as baseUrl } from 'src/utils/api-url';
import { buildExportParams } from './xbitRelayExport.helpers';

const resource = 'xbit-relay/withdrawals';

async function authHeaders() {
  const { accessToken } = (await getAuth()) || {};
  return {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/json',
  };
}

export type WithdrawalStatus =
  | 'Pending'
  | 'Reviewing'
  | 'PendingAdminApproval'
  | 'Broadcasting'
  | 'Executing'
  | 'Confirming'
  | 'Completed'
  | 'Rejected'
  | 'Failed';

export type WithdrawalProvider = 'PERPETUAL' | 'INTERNAL_PERPETUAL' | 'TAP_TRADING';

export type WithdrawalItem = {
  id: string;
  userId: string;
  nonce?: string;
  toAddress: string;
  chainId: string | number;
  token: string;
  symbol?: string;
  tokenSymbol?: string;
  amount: string;
  platformFee: string;
  status: WithdrawalStatus | string;
  provider: WithdrawalProvider | string;
  createdAtUnixMs: string;
  updatedAtUnixMs?: string;
  txHash?: string;
  txId?: string;
  errorMessage?: string;
  errorCode?: string;
  errorMsg?: string;
  lockId?: string;
};

export type ListWithdrawalsResponse = {
  items: WithdrawalItem[];
  total: string;
  page: number;
  pageSize: number;
};

export type WithdrawalListFilter = {
  page?: number;
  pageSize?: number;
  provider?: WithdrawalProvider | string;
  status?: WithdrawalStatus | string;
  userId?: string;
  recordId?: string;
  toAddress?: string;
  txHash?: string;
  timeFrom?: number;
  timeTo?: number;
};

export async function listWithdrawals(filter: WithdrawalListFilter = {}) {
  const headers = await authHeaders();
  const params: Record<string, string> = {};
  Object.entries(filter).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    params[k] = String(v);
  });
  return axios.get<ListWithdrawalsResponse>(`${baseUrl}/${resource}`, {
    headers,
    params,
  });
}

export type ApproveWithdrawalBody = {
  admin_id: string;
  signature: string;
  timestamp_ms: number;
};

export type ApproveWithdrawalResponse = {
  success?: boolean;
  message?: string;
};

export async function approveWithdrawal(
  id: string,
  adminId: string,
  signature: string,
  timestampMs: number,
) {
  const headers = await authHeaders();
  return axios.post<ApproveWithdrawalResponse>(
    `${baseUrl}/${resource}/${id}/approve`,
    { admin_id: adminId, signature, timestamp_ms: timestampMs },
    { headers },
  );
}

export type RejectWithdrawalBody = {
  admin_id: string;
  signature: string;
  timestamp_ms: number;
  note?: string;
};

export type RejectWithdrawalResponse = {
  success?: boolean;
  message?: string;
};

export async function rejectWithdrawal(
  id: string,
  body: RejectWithdrawalBody,
) {
  const headers = await authHeaders();
  return axios.post<RejectWithdrawalResponse>(
    `${baseUrl}/${resource}/${id}/reject`,
    body,
    { headers },
  );
}

export type RetryFailedWithdrawalBody = {
  admin_id: string;
  signature: string;
  timestamp: number;
  evm_nonce?: string;
  gas_limit?: string;
  gas_price?: string;
  fee_limit?: string;
};

export type RetryFailedWithdrawalResponse = {
  success: boolean;
  message: string;
  txId: string;
};

export async function retryFailedWithdrawal(
  id: string,
  body: RetryFailedWithdrawalBody,
) {
  const headers = await authHeaders();
  return axios.post<RetryFailedWithdrawalResponse>(
    `${baseUrl}/${resource}/${id}/retry-failed`,
    body,
    { headers },
  );
}

export type CancelFailedWithdrawalBody = {
  admin_id: string;
  signature: string;
  timestamp: number;
  idempotency_key?: string;
  note?: string;
};

export type CancelFailedWithdrawalResponse = {
  success: boolean;
  message: string;
  duplicate: boolean;
};

export async function cancelFailedWithdrawal(
  id: string,
  body: CancelFailedWithdrawalBody,
) {
  const headers = await authHeaders();
  return axios.post<CancelFailedWithdrawalResponse>(
    `${baseUrl}/${resource}/${id}/cancel-failed`,
    body,
    { headers },
  );
}

export type MarkWithdrawalDoneBody = {
  admin_id: string;
  signature: string;
  timestamp_ms: number;
  note?: string;
};

export type MarkWithdrawalDoneResponse = {
  success: boolean;
  message: string;
};

export async function markWithdrawalDone(
  id: string,
  body: MarkWithdrawalDoneBody,
) {
  const headers = await authHeaders();
  return axios.post<MarkWithdrawalDoneResponse>(
    `${baseUrl}/${resource}/${id}/mark-done`,
    body,
    { headers },
  );
}

export type ExportWithdrawalsFilter = {
  provider?: WithdrawalProvider | string;
  timeFrom?: number;
  timeTo?: number;
  status?: WithdrawalStatus | string;
  userId?: string;
};

// Withdrawals export supports ONLY provider/timeFrom/timeTo/status/userId.
// recordId/toAddress/txHash are NOT supported at the gRPC/DB layer and must never be sent.
export async function exportWithdrawals(
  filter: ExportWithdrawalsFilter = {},
): Promise<Blob> {
  const headers = await authHeaders();
  const params = buildExportParams({
    provider: filter.provider,
    timeFrom: filter.timeFrom,
    timeTo: filter.timeTo,
    status: filter.status,
    userId: filter.userId,
  });
  const res = await axios.get<Blob>(`${baseUrl}/${resource}/export`, {
    headers,
    params,
    responseType: 'blob',
  });
  return res.data;
}
